import { z } from "zod";

// reusable enum constants
export const OperatorEnum = z.enum(["gt", "lt", "eq", "ge", "le"]);
export const HotspotTypeEnum = z.enum([
  "rect",
  "circle",
  "polygon",
  "text_gap",
]);
export const GapTypeEnum = z.enum([
  "inline-choice",
  "inline-choice-richtext",
  "text-entry",
  "extended-entry",
  "math-entry",
  "numeric-entry",
]);
export const MathKeyboardPresetEnum = z.enum(["elementary", "secondary"]);
export const MarkEnum = z.enum(["correct", "incorrect"]);
export const TokenScaleEnum = z.enum(["@1x", "@2x", "@3x", "@300dpi"]);
export const MatchTableScoringMethodEnum = z.enum(["EXACT", "PARTIAL_PER_ROW"]);
export const GapMatchScoringMethodEnum = z.enum([
  "TOKEN_PLACEMENT",
  "GAP_VALUE",
]);
export const MatchStrategyEnum = z.enum(["exact", "contains"]);
export const FractionShapeEnum = z.enum(["pie", "bar"]);

// cartesian shape enums and schemas
export const CartesianShapeTypeEnum = z.enum([
  "circle",
  "point",
  "polygon",
  "line",
  "dashed-line",
  "line-segment",
  "line-segment-right-open",
  "line-segment-left-open",
  "line-segment-both-open",
  "ray",
  "ray-left",
  "ray-left-open",
  "ray-right",
  "ray-right-open",
]);

export const CartesianCoordinateSchema = z.object({
  x: z.number(),
  y: z.number(),
});

const CartesianShapeBaseSchema = z.object({
  type: CartesianShapeTypeEnum,
  isBackground: z.boolean().optional(),
});

export const CartesianShapeSchema = z.discriminatedUnion("type", [
  // point shape
  CartesianShapeBaseSchema.extend({
    type: z.literal("point"),
    coord: CartesianCoordinateSchema,
  }),
  // circle shape
  CartesianShapeBaseSchema.extend({
    type: z.literal("circle"),
    coords: z.array(CartesianCoordinateSchema),
  }),
  // polygon shape
  CartesianShapeBaseSchema.extend({
    type: z.literal("polygon"),
    coords: z.array(CartesianCoordinateSchema),
  }),
  // line shape
  CartesianShapeBaseSchema.extend({
    type: z.literal("line"),
    coords: z.array(CartesianCoordinateSchema),
  }),
  // dashed line shape
  CartesianShapeBaseSchema.extend({
    type: z.literal("dashed-line"),
    coords: z.array(CartesianCoordinateSchema),
  }),
  // line segment shapes
  CartesianShapeBaseSchema.extend({
    type: z.literal("line-segment"),
    coords: z.array(CartesianCoordinateSchema),
  }),
  CartesianShapeBaseSchema.extend({
    type: z.literal("line-segment-right-open"),
    coords: z.array(CartesianCoordinateSchema),
  }),
  CartesianShapeBaseSchema.extend({
    type: z.literal("line-segment-left-open"),
    coords: z.array(CartesianCoordinateSchema),
  }),
  CartesianShapeBaseSchema.extend({
    type: z.literal("line-segment-both-open"),
    coords: z.array(CartesianCoordinateSchema),
  }),
  // ray shapes
  CartesianShapeBaseSchema.extend({
    type: z.literal("ray"),
    coords: z.array(CartesianCoordinateSchema),
  }),
  CartesianShapeBaseSchema.extend({
    type: z.literal("ray-left"),
    coord: CartesianCoordinateSchema,
  }),
  CartesianShapeBaseSchema.extend({
    type: z.literal("ray-left-open"),
    coord: CartesianCoordinateSchema,
  }),
  CartesianShapeBaseSchema.extend({
    type: z.literal("ray-right"),
    coord: CartesianCoordinateSchema,
  }),
  CartesianShapeBaseSchema.extend({
    type: z.literal("ray-right-open"),
    coord: CartesianCoordinateSchema,
  }),
]);

// typeScript type aliases for interoperability
export type Operator = z.infer<typeof OperatorEnum>;
export type HotspotType = z.infer<typeof HotspotTypeEnum>;
export type GapType = z.infer<typeof GapTypeEnum>;
export type MathKeyboardPreset = z.infer<typeof MathKeyboardPresetEnum>;
export type Mark = z.infer<typeof MarkEnum>;
export type TokenScale = z.infer<typeof TokenScaleEnum>;
export type MatchTableScoringMethod = z.infer<
  typeof MatchTableScoringMethodEnum
>;
export type GapMatchScoringMethod = z.infer<typeof GapMatchScoringMethodEnum>;
export type MatchStrategy = z.infer<typeof MatchStrategyEnum>;
export type CartesianShapeType = z.infer<typeof CartesianShapeTypeEnum>;
export type CartesianCoordinate = z.infer<typeof CartesianCoordinateSchema>;
export type CartesianShape = z.infer<typeof CartesianShapeSchema>;

// base schemas that match item-stem.ts interfaces

export const ResourceFileSchema = z.object({
  name: z.string().optional(),
  region: z.string().optional(),
  bucket: z.string().optional(),
  key: z.string().optional(),
  size: z.number().optional(),
  mimetype: z.string().optional(),
  format: z.string().optional(),
  extension: z.string().optional(),
});

export const A11ySchema = z.object({
  speech: z
    .object({
      enabled: z.boolean(),
      textType: z.string().optional(),
      voiceId: z.string().optional(),
      ssml: z.string().optional(),
      text: z.string().optional(),
      file: ResourceFileSchema.partial().optional(),
    })
    .optional(),
  audio: z
    .object({
      enabled: z.boolean(),
      file: ResourceFileSchema.partial().optional(),
    })
    .optional(),
  asl: z
    .object({
      enabled: z.boolean(),
      file: ResourceFileSchema.partial().optional(),
    })
    .optional(),
});

export const AudioSchema = z.object({
  textType: z.string(),
  voiceId: z.string(),
  ssml: z.string(),
  text: z.string(),
});

export const TextSchema = z.object({
  body: z.string().optional(),
  audio: AudioSchema.optional(),
  a11y: A11ySchema.optional(),
});

export const PromptSchema = TextSchema.extend({
  language: z.string().optional(),
});

export const ImageMetadataSchema = z.object({
  format: z.string(),
  height: z.number(),
  width: z.number(),
  size: z.number(),
  scale: z.union([z.string(), TokenScaleEnum]),
  alt: z.string().optional(),
});

export const ImageSchema = z.object({
  metadata: ImageMetadataSchema,
  file: z.string(),
  folder: z.string(),
  filename: z.string(),
});

export const TokenSchema = z.object({
  id: z.string(),
  src: z.string(),
  metadata: ImageMetadataSchema,
  mark: MarkEnum.optional(),
  maxUsage: z.number().optional(),
  numericValue: z.number().optional(),
});

export const OptionSchema = z.object({
  id: z.string(),
  body: z.string().optional(),
  a11y: A11ySchema.optional(),
  isKey: z.boolean().optional(),
  rationale: z.string().optional(),
  score: z.number().optional(),
  maxUsage: z.number().optional(),
  numericValue: z.number().optional(),
  fontSize: z.string().optional(),
});

export const CoordinatesSchema = z.object({
  x: z.number(),
  y: z.number(),
  w: z.number().optional(),
  h: z.number().optional(),
  r: z.number().optional(),
});

export const SpotSchema = z.object({
  id: z.string(),
  shape: HotspotTypeEnum,
  coords: CoordinatesSchema,
  props: z.any().optional(),
  allowMultipleTokens: z.boolean().optional(),
});

export const GapSchema = z.object({
  id: z.string().optional(),
  type: GapTypeEnum,
  options: z.array(OptionSchema),
  mathKeyboardPreset: MathKeyboardPresetEnum.optional(),
  body: z.string().optional(),
  groupId: z.string().optional(),
  allowMultipleTokens: z.boolean().optional(),
  maxTokens: z.number().optional(),
  fontSize: z.string().optional(),
  textAlign: z.string().optional(),
  precision: z.string().optional(),
  inputWidth: z.number().optional(),
  matchStrategy: MatchStrategyEnum.optional(),
  matchCase: z.boolean().optional(),
});

export const PairSchema = z.object({
  id: z.string().optional(),
  src: z.string().optional(),
  dst: z.string().optional(),
  isKey: z.boolean().optional(),
  gapValue: z.number().optional(),
});

export const DropSchema = z.object({
  instanceId: z.string(),
  draggableId: z.string(),
  droppableId: z.string(),
  coords: z
    .object({
      x: z.number(),
      y: z.number(),
    })
    .optional(),
});

// config schemas
export const ConfigTogglesSchema = z.object({
  enableCustomScoring: z.boolean().optional(),
  allowMultipleKeys: z.boolean().optional(),
  enableRationales: z.boolean().optional(),
  enableRubric: z.boolean().optional(),
  enableSolution: z.boolean().optional(),
  enableShuffle: z.boolean().optional(),
});

export const ConfigSchema = z
  .object({
    // standard config
    enableCustomScoring: z.boolean().optional(),
    allowMultipleKeys: z.boolean().optional(),
    enableRationales: z.boolean().optional(),
    enableRubric: z.boolean().optional(),
    enableSolution: z.boolean().optional(),
    enableShuffle: z.boolean().optional(),

    // scoring-related
    minScore: z.union([z.number(), z.string()]).optional(),
    maxScore: z.union([z.number(), z.string()]).optional(),
    defaultScore: z.union([z.number(), z.string()]).optional(),
    maxChoices: z.union([z.number(), z.string()]).optional(),
    minChoices: z.union([z.number(), z.string()]).optional(),
    matchTableGridScoringMethod: MatchTableScoringMethodEnum.optional(),

    layout: z
      .object({
        hasPlusMinusColumn: z.boolean().optional(),
        rangeMax: z.number().optional(),
        rangeMin: z.number().optional(),
        columnCount: z.number().optional(),
        hasFloatingDecimal: z.boolean().optional(),
      })
      .optional(),

    print: z
      .object({
        numberOfLines: z.number().optional(),
        showLines: z.boolean().optional(),
        showBoxOutline: z.boolean().optional(),
      })
      .optional(),

    graph: z
      .object({
        padding: z
          .object({
            top: z.number(),
            left: z.number(),
            bottom: z.number(),
            right: z.number(),
          })
          .optional(),
        shapes: z.array(CartesianShapeSchema).optional(),
        width: z.number().optional(),
        height: z.number().optional(),
        x: z
          .object({
            min: z.number(),
            max: z.number(),
            label: z.string(),
          })
          .optional(),
        y: z
          .object({
            min: z.number(),
            max: z.number(),
            label: z.string(),
          })
          .optional(),
        title: z.string().optional(),
      })
      .optional(),
  })
  // TODO: remove catchall after solidifying content schemas
  .catchall(z.any());

// scoring schemas
export const ScoringSchema = z
  .object({
    solution: z
      .object({
        body: z.string(),
      })
      .optional(),
    rubric: z
      .object({
        body: z.string(),
      })
      .optional(),
    options: z.record(z.string(), OptionSchema).optional(),
    gaps: z
      .record(
        z.string(),
        z.object({
          id: z.string().optional(),
          options: z.record(z.string(), OptionSchema),
        }),
      )
      .optional(),
    spots: z.record(z.string(), OptionSchema).optional(),
    pairs: z.array(PairSchema).optional(),
    order: z.array(z.string()).optional(),
    values: z.array(z.string()).optional(),
    shapes: z.array(CartesianShapeSchema).optional(),
    matchOptions: z
      .record(
        z.string(),
        z.record(
          z.string(),
          z.object({
            id: z.string(),
            isKey: z.boolean(),
          }),
        ),
      )
      .optional(),
    shaded: z
      .array(
        z.object({
          isKey: z.boolean(),
          op: OperatorEnum,
          value: z.string(),
        }),
      )
      .optional(),
    slider: z
      .array(
        z.object({
          isKey: z.boolean(),
          op: OperatorEnum,
          value: z.string(),
        }),
      )
      .optional(),
    inequality: z
      .object({
        quadrant: z.number(),
      })
      .optional(),
    gapMatchScoringMethod: GapMatchScoringMethodEnum.optional(),
    // chart scoring data
    data: z
      .array(
        z.object({
          value: z.number(),
          op: OperatorEnum,
        }),
      )
      .optional(),
  })
  // TODO: remove catchall after solidifying content schemas
  .catchall(z.any());

// response schemas
export const ResponseGapsSchema = z.record(z.string());

export const ResponseSchema = z.object({
  options: z.array(z.string()).optional(),
  maskedOptions: z.array(z.string()).optional(),
  gaps: ResponseGapsSchema.optional(),
  order: z.array(z.string()).optional(),
  spots: z.array(z.string()).optional(),
  pairs: z.union([z.array(z.string()), z.array(PairSchema)]).optional(),
  coords: CoordinatesSchema.optional(),
  drops: z.array(DropSchema).optional(),
  shapes: z.array(CartesianShapeSchema).optional(),
  shaded: z.array(z.array(z.number())).optional(),
  shadedWithGroups: z.array(z.array(z.array(z.number()))).optional(),
  chart: z
    .array(
      z.object({
        value: z.number(),
      }),
    )
    .optional(),
  matchTable: z.record(z.array(z.string())).optional(),
  inequality: z
    .object({
      quadrant: z.number(),
    })
    .optional(),
  drawing: z
    .object({
      blobId: z.string(),
      checksum: z.string().optional(),
    })
    .optional(),
});

// content schemas
export const ContentSchema = z
  .object({
    prompt: PromptSchema,
    spots: z.array(SpotSchema).optional(),
    options: z.array(OptionSchema).optional(),
    optionsOne: z.array(OptionSchema).optional(),
    optionsTwo: z.array(OptionSchema).optional(),
    stems: z.array(OptionSchema).optional(),
    text: TextSchema.partial().optional(),
    gaps: z.record(GapSchema).optional(),
    tokens: z.array(TokenSchema).optional(),
    image: ImageSchema.optional(),

    // chart data
    data: z
      .array(
        z.object({
          label: z.string(),
          value: z.number(),
        }),
      )
      .optional(),
  })
  // TODO: remove catchall after solidifying content schemas
  .catchall(z.any());
